import React, { useState } from "react";

/**
 * SON — Home page
 * Built directly on top of the SON Design System tokens:
 * background #F7F5F2 · primary #351514 · secondary #7F6A60 · accent #F2DB94
 * display: Playfair Display · body: Inter · button/input radius 12px · card 16px · modal 20px
 */

const tokens = {
  background: "#F7F5F2",
  surface: "#FFFFFF",
  primary: "#351514",
  primaryLight: "#532D2C",
  primaryDark: "#1D0605",
  secondary: "#7F6A60",
  accent: "#F2DB94",
  textPrimary: "#2F2F2F",
  textSecondary: "#5A5A5A",
  border: "#E8E3DD",
  success: "#4CAF50",
};

const display = { fontFamily: "'Playfair Display', serif" };
const body = { fontFamily: "'Inter', sans-serif" };

const Icon = ({ name, className = "w-5 h-5" }) => {
  const icons = {
    dashboard: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </svg>
    ),
    projetos: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    crm: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    financeiro: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    marketing: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    ),
    documentos: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
      </svg>
    ),
    agenda: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    ia: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    comunidade: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
      </svg>
    ),
    check: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ),
    arrow: (
      <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    ),
  };
  return icons[name] || null;
};

const modules = [
  { icon: "dashboard", label: "Dashboard", desc: "A visão do seu dia inteiro, sem precisar procurar." },
  { icon: "crm", label: "CRM", desc: "Cada lead e cliente organizado num só funil." },
  { icon: "financeiro", label: "Financeiro", desc: "Faturamento, custos e CAC sempre à vista." },
  { icon: "projetos", label: "Projetos", desc: "O andamento de cada entrega, sem planilha paralela." },
  { icon: "marketing", label: "Marketing", desc: "Campanhas e resultados no mesmo painel." },
  { icon: "agenda", label: "Agenda", desc: "Reuniões e compromissos, sem choque de horário." },
  { icon: "documentos", label: "Documentos", desc: "Contratos e arquivos importantes, fáceis de achar." },
  { icon: "ia", label: "Marketing", desc: "Cronograma de conteudo e linha editorial." },
];

const pillars = [
  { title: "Clareza", desc: "A informação se entende em poucos segundos." },
  { title: "Organização", desc: "Cada elemento tem um propósito lógico, sem excesso." },
  { title: "Segurança", desc: "A interface transmite confiança nos dados." },
  { title: "Eficiência", desc: "Menos cliques, mais ação direta." },
];

function Button({ variant = "primary", size = "md", className = "", children, ...props }) {
  const sizes = { sm: { padding: "9px 16px", fontSize: 14 }, md: { padding: "12px 22px", fontSize: 16 }, lg: { padding: "14px 28px", fontSize: 16 } };
  const base = {
    ...body,
    fontWeight: 600,
    borderRadius: 12,
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "all 200ms cubic-bezier(0.4,0,0.2,1)",
    ...sizes[size],
  };
  const variants = {
    primary: { background: tokens.primary, color: "#fff", boxShadow: "0 2px 8px rgba(53,21,20,0.15)" },
    accent: { background: tokens.accent, color: tokens.primary },
    outline: { background: "transparent", color: "#fff", border: "1.5px solid rgba(255,255,255,0.5)" },
    secondary: { background: "#fff", color: tokens.primary, border: `1px solid ${tokens.primary}` },
  };
  return (
    <button
      style={{ ...base, ...variants[variant] }}
      className={`hover:opacity-90 active:scale-[0.98] ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export default function SonHomePage() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div style={{ ...body, background: tokens.background, color: tokens.textPrimary }} className="min-h-screen w-full">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap');
      `}</style>

      {/* NAV */}
      <header style={{ background: tokens.primary }} className="sticky top-0 z-40 shadow-md">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div style={{ background: tokens.accent, color: tokens.primary, ...display }} className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-lg">S</div>
            <span style={{ ...display, color: "#fff" }} className="text-xl font-bold tracking-tight">SON</span>
          </div>

          <nav className="hidden md:flex items-center gap-8">

  <a 
    href="#produto"
    style={{ color: "rgba(255,255,255,0.8)" }}
    className="text-sm font-medium hover:text-white transition-colors"
  >
    Produto
  </a>

  <a 
    href="#modulos"
    style={{ color: "rgba(255,255,255,0.8)" }}
    className="text-sm font-medium hover:text-white transition-colors"
  >
    Módulos
  </a>

  <a 
    href="#precos"
    style={{ color: "rgba(255,255,255,0.8)" }}
    className="text-sm font-medium hover:text-white transition-colors"
  >
    Preços
  </a>

  <a 
    href="#sobre"
    style={{ color: "rgba(255,255,255,0.8)" }}
    className="text-sm font-medium hover:text-white transition-colors"
  >
    Sobre
  </a>

</nav>

          <div className="hidden md:flex items-center gap-3">
            <a href="/login" style={{ color: "rgba(255,255,255,0.85)" }} className="text-sm font-semibold hover:text-white">Entrar</a>
            <a href="/cadastro"><Button variant="accent" size="sm">Começar agora</Button> </a>
          </div>

          <button className="md:hidden text-white" onClick={() => setMenuOpen((v) => !v)} aria-label="Menu">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {menuOpen && (
          <div style={{ background: tokens.primaryLight }} className="md:hidden px-6 py-4 flex flex-col gap-3">
            {["Produto", "Módulos", "Preços", "Sobre", "Entrar"].map((item) => (
              <a key={item} href="#" style={{ color: "rgba(255,255,255,0.9)" }} className="text-sm font-medium">{item}</a>
            ))}
           <a href="/cadastro"> <Button variant="accent" size="sm" className="w-full mt-1">Começar agora</Button> </a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section   id="produto" className="max-w-6xl mx-auto px-6 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <span
            style={{ background: "#fff", color: tokens.secondary, border: `1px solid ${tokens.border}` }}
            className="inline-block text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5"
          >
            Sistema operacional do empreendedor
          </span>
          <h1 style={{ ...display, color: tokens.primary }} className="text-4xl sm:text-5xl font-bold leading-[1.1] mb-5">
            Menos ruído.
            <br />
            Mais clareza pra decidir.
          </h1>
          <p style={{ color: tokens.textSecondary }} className="text-base sm:text-lg leading-relaxed max-w-md mb-8">
            SON reúne CRM, financeiro, projetos, marketing e agenda num único painel. Toda tela responde em segundos:
            onde você está, o que pode fazer e qual é a próxima ação.
          </p>
          <div className="flex flex-wrap gap-3 mb-8">
           <a href="/cadastro">   <Button variant="primary" size="lg">
              Criar conta grátis <Icon name="arrow" className="w-4 h-4" />
            </Button> </a>
         <a href="/ShowcaseApp">      <Button variant="secondary" size="lg">Ver demonstração</Button> </a>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {["JU", "MA", "PE", "CA"].map((i) => (
                <div key={i} style={{ background: tokens.accent, color: tokens.primary, borderColor: tokens.background }} className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[10px] font-bold">
                  {i}
                </div>
              ))}
            </div>
            <p style={{ color: tokens.textSecondary }} className="text-xs">
              <strong style={{ color: tokens.textPrimary }}> muitos empreendedores</strong> já organizam o negócio no SON
            </p>
          </div>
        </div>

        {/* Signature element: live-feeling dashboard preview */}
        <div style={{ background: "#fff", border: `1px solid ${tokens.border}`, borderRadius: 20, boxShadow: "0 12px 48px rgba(53,21,20,0.12)" }} className="p-6 sm:p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p style={{ ...display, color: tokens.textPrimary }} className="text-lg font-bold">Bom dia, Juliana</p>
              <p style={{ color: tokens.textSecondary }} className="text-xs">Terça-feira, 14 de julho</p>
            </div>
            <div style={{ background: tokens.accent, color: tokens.primary }} className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold">JU</div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-5">
            {[["Faturamento", "R$ 45,2k"], ["Leads", "18.6%"], ["CAC", "R$ 142"]].map(([label, val]) => (
              <div key={label} style={{ background: tokens.background, borderRadius: 12 }} className="p-3">
                <p style={{ color: tokens.textSecondary }} className="text-[9px] font-semibold uppercase tracking-wide">{label}</p>
                <p style={{ color: tokens.primary, ...display }} className="text-sm font-bold mt-0.5">{val}</p>
              </div>
            ))}
          </div>

          <div style={{ border: `1px solid ${tokens.border}`, borderRadius: 12 }} className="p-4 mb-4">
            <p style={{ color: tokens.textSecondary }} className="text-[10px] font-bold uppercase tracking-wider mb-3">Desempenho semanal</p>
            <div className="h-20 flex items-end justify-between gap-1.5">
              {[40, 55, 48, 70, 62, 88, 95].map((v, i) => (
                <div key={i} style={{ height: `${v}%`, background: i === 6 ? tokens.primary : tokens.accent, borderRadius: "4px 4px 0 0" }} className="flex-1" />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span style={{ background: `${tokens.success}20`, color: tokens.success }} className="w-6 h-6 rounded-full flex items-center justify-center shrink-0">
              <Icon name="check" className="w-3.5 h-3.5" />
            </span>
            <p style={{ color: tokens.textSecondary }} className="text-xs">3 tarefas concluídas antes das 10h</p>
          </div>
        </div>
      </section>

      {/* THREE QUESTIONS */}
      <section style={{ background: "#fff", borderTop: `1px solid ${tokens.border}`, borderBottom: `1px solid ${tokens.border}` }}>
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 style={{ ...display, color: tokens.primary }} className="text-2xl sm:text-3xl font-bold mb-2">
            Toda tela responde três perguntas
          </h2>
          <p style={{ color: tokens.textSecondary }} className="text-sm mb-10 max-w-xl">
            É o teste que aplicamos antes de qualquer tela entrar no produto — se não responde isso em segundos, ela volta pra mesa de desenho.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { q: "Onde estou?", a: "Localização e módulo claros de imediato." },
              { q: "O que posso fazer aqui?", a: "Opções e fluxos de trabalho visíveis, sem ruído." },
              { q: "Qual a próxima ação?", a: "Botões e guias claros indicando o caminho." },
            ].map((item, i) => (
              <div key={item.q} style={{ background: tokens.background, borderRadius: 16, border: `1px solid ${tokens.border}` }} className="p-6">
                <span style={{ color: tokens.secondary }} className="text-xs font-bold tracking-wider">{`0${i + 1}`}</span>
                <p style={{ ...display, color: tokens.textPrimary }} className="font-bold text-lg mt-2 mb-1.5">{item.q}</p>
                <p style={{ color: tokens.textSecondary }} className="text-sm leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section id="modulos" className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-xl mb-10">
          <span style={{ color: tokens.secondary }} className="text-xs font-bold uppercase tracking-wider">Módulos</span>
          <h2 style={{ ...display, color: tokens.primary }} className="text-2xl sm:text-3xl font-bold mt-2 mb-2">
            Um só lugar pra cada parte do negócio
          </h2>
          <p style={{ color: tokens.textSecondary }} className="text-sm leading-relaxed">
            Sem trocar de aba entre cinco ferramentas diferentes. Tudo com a mesma linguagem visual, a mesma lógica de uso.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {modules.map((m) => (
            <div
              key={m.label}
              style={{ background: "#fff", border: `1px solid ${tokens.border}`, borderRadius: 16 }}
              className="p-5 hover:shadow-md transition-shadow duration-200"
            >
              <div style={{ background: tokens.background, color: tokens.primary }} className="w-10 h-10 rounded-lg flex items-center justify-center mb-4">
                <Icon name={m.icon} className="w-5 h-5" />
              </div>
              <p style={{ color: tokens.textPrimary }} className="font-bold text-sm mb-1">{m.label}</p>
              <p style={{ color: tokens.textSecondary }} className="text-xs leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section  id="sobre" style={{ background: tokens.primary }} className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2  style={{ ...display, color: "#fff" }} className="text-2xl sm:text-3xl font-bold mb-10">
            Os quatro pilares do SON
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((p, i) => (
              <div key={p.title}>
                <span style={{ background: "rgba(242,219,148,0.15)", color: tokens.accent }} className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm mb-4">
                  {i + 1}
                </span>
                <p style={{ ...display, color: "#fff" }} className="font-bold text-lg mb-1.5">{p.title}</p>
                <p style={{ color: "rgba(255,255,255,0.65)" }} className="text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section   id="precos" className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 style={{ ...display, color: tokens.primary }} className="text-3xl sm:text-4xl font-bold mb-4">
          Pronto pra ter clareza no seu negócio?
        </h2>
        <p style={{ color: tokens.textSecondary }} className="text-base mb-8 max-w-md mx-auto">
          Comece grátis hoje. Sem cartão de crédito, sem instalação, sem complicação.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
        <a href="/cadastro">  <Button variant="primary" size="lg">Criar conta grátis</Button> </a>
          <Button variant="secondary" size="lg">Falar com o time</Button>
        </div>
      </section>

      {/* FOOTER */}
      <footer   style={{ borderTop: `1px solid ${tokens.border}` }} className="py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div style={{ background: tokens.primary, color: "#fff", ...display }} className="w-7 h-7 rounded-md flex items-center justify-center font-bold text-sm">S</div>
            <span style={{ ...display, color: tokens.primary }} className="font-bold">SON</span>
          </div>
          <p style={{ color: tokens.textSecondary }} className="text-xs">© 2026 SON. Todos os direitos reservados. · Design System v1.0.0</p>
        </div>
      </footer>
    </div>
  );
}